import {
    callRecordIsIncoming,
    getCallRecordInCallDuration,
    getCallRecordInQueueDuration,
    isCallRecordAMeeting,
} from 'flow/utils/call-records-utils';
import { formatDurationMs } from 'flow/utils/duration-utils';
import ContactTitle from './ContactTitle';
import ContactSubtitle from './ContactSubtitle';
import QueueCallDurations from './QueueCallDurations';
import QueueCallInfo from './QueueCallInfo';
import TransferredByInfo from './TransferredByInfo';
import { getContactFullName } from 'flow/utils/call-record-utils';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectIsSinglePaneMode } from 'flow/selectors/app-selectors';
import { selectCallRecordChatSessionDisplayName } from 'flow/selectors/call-selectors';
import { useCallRecordWithCtiContactByKey } from 'flow/hooks/useCallRecordWithCtiContact';
import { PageType } from 'flow/components/call-history-v2/types';
import type { i18n as I18n } from 'i18next';

interface Props {
    callRecordKey: CallRecordKey;
    queueKeyContext?: QueueKey;
    showCaller?: boolean;
    compact?: boolean;
    pageType: PageType;
}

const CallRecordDetails = ({
    callRecordKey,
    queueKeyContext,
    showCaller,
    compact,
    pageType,
}: Props) => {
    const { i18next } = useAppContext();
    const isSinglePane = useAppSelector(selectIsSinglePaneMode);
    const callRecord = useCallRecordWithCtiContactByKey(callRecordKey);
    const videoDisplayName = useAppSelector((state) =>
        selectCallRecordChatSessionDisplayName(state, callRecordKey),
    );

    if (!callRecord) return null;

    const useCompactFormat = compact && !isSinglePane;
    const contactFullName = videoDisplayName || getContactFullName(callRecord);
    const isVideoCall = isCallRecordAMeeting(callRecord);

    const callDurationText = formatDurationMs(
        getCallRecordInCallDuration(callRecord),
        useCompactFormat,
        i18next,
    );
    const waitTimeText = formatDurationMs(
        getCallRecordInQueueDuration(callRecord),
        useCompactFormat,
        i18next,
    );

    const queueKey = callRecord.getIn(['queue', 'key']);
    const queueName = callRecord.getIn(['queue', 'description']);
    const isQueueCall = !!queueKey;

    const showSplitDurations =
        showCaller && isQueueCall && !!callDurationText && !!waitTimeText;

    const durationText = showSplitDurations
        ? undefined
        : getDurationText({
              isQueueCall,
              callDurationText,
              waitTimeText,
              i18next,
          });
    const showQueueName = queueName && queueKey !== queueKeyContext;
    const agentFirstName = callRecord.getIn(['agent', 'contact', 'firstName']);
    const agentLastName = callRecord.getIn(['agent', 'contact', 'lastName']);
    const agentFullName = [agentFirstName, agentLastName]
        .filter(Boolean)
        .join(' ');

    return (
        <div className="flex flex-col text-sm leading-6 break-words text-gray-500 antialiased">
            {durationText && <span>{durationText}</span>}
            {showSplitDurations && (
                <QueueCallDurations
                    waitTimeText={waitTimeText}
                    callDurationText={callDurationText}
                />
            )}
            {showCaller && (
                <span>
                    {callRecordIsIncoming(callRecord)
                        ? `${i18next.t('from')} `
                        : `${i18next.t('call.to')} `}
                    <span className="text-gray-700">
                        <ContactTitle
                            contact={callRecord.get('contact')}
                            contactFullName={contactFullName}
                            number={callRecord.get('number')}
                            hideIcon={true}
                        />
                    </span>
                    {!isVideoCall && (
                        <>
                            {' '}
                            <ContactSubtitle
                                contact={callRecord.get('contact')}
                                contactFullName={contactFullName}
                                number={callRecord.get('number')}
                                inline={true}
                            />
                        </>
                    )}
                </span>
            )}
            <QueueCallInfo
                queueName={showQueueName ? queueName : undefined}
                agentFullName={agentFullName}
                compact={compact}
                showAnsweringAgent={pageType !== 'personal'}
            />
            <TransferredByInfo
                callRecordKey={callRecordKey}
                compact={compact}
                showContactDetails={showCaller}
            />
        </div>
    );
};

const getDurationText = ({
    isQueueCall,
    callDurationText,
    waitTimeText,
    i18next,
}: {
    isQueueCall: boolean;
    callDurationText?: string;
    waitTimeText?: string;
    i18next: I18n;
}) => {
    if (!isQueueCall) return callDurationText;
    if (callDurationText && waitTimeText) {
        return i18next.t('Call.Duration.AnsweredQueue', {
            callDuration: callDurationText,
            waitTime: waitTimeText,
        });
    }
    if (waitTimeText) {
        return i18next.t('Call.Duration.InQueue', {
            waitTime: waitTimeText,
        });
    }
    return undefined;
};

export default CallRecordDetails;
